'use client';

import React, { useContext, useState } from 'react';
import { styled } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import MuiLink from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Paper from '@mui/material/Paper';
import ErrorBoundary from '../ErrorBoundary';
import AIFeedbackThumbs from '../AIFeedbackThumbs';
import { useAISearch, SourceDocument } from '../../hooks/useAISearch';
import { AuthContext } from '../AuthProvider/AuthProvider';
import { useLocalizationContext } from '../LocalizationContext';
import { safeDataLayerPush } from '../../utils/safeDataLayerPush';

export interface AISearchResultProps {
  query: string;
  chatflowid: string;
  apiHost: string;
  enabled: boolean;
}

function AISearchResult({ query, chatflowid, apiHost, enabled }: AISearchResultProps) {
  const localization = useLocalizationContext();
  const auth = useContext(AuthContext);
  const userEmail = auth?.userEmail || undefined;
  const [showTextFeedback, setShowTextFeedback] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [isSubmittingText, setIsSubmittingText] = useState(false);
  const [feedbackId, setFeedbackId] = useState<string | null>(null);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Reset dismissed and expanded state when query changes
  React.useEffect(() => {
    setIsDismissed(false);
    setIsExpanded(false);
  }, [query]);

  const { data, isLoading, error } = useAISearch({
    query,
    chatflowid,
    apiHost,
    userEmail,
    trackingData: auth?.trackingData,
    authType: auth?.authType,
    enabled: enabled && !!query.trim()
  });

  // Don't render if disabled, no query, or dismissed
  if (!enabled || !query.trim() || isDismissed) {
    return null;
  }

  // Show loading skeleton
  if (isLoading) {
    return (
      <ErrorBoundary>
        <AIResultContainer data-testid="AISearchResult-loading">
          <AIResultHeader>
            <AutoAwesomeIcon sx={{ fontSize: 20, color: 'primary.main' }} />
            <Typography variant="subtitle2" fontWeight={600}>
              {localization['aiSearch.title.label']?.shortTextValue || 'AI Answer'}
            </Typography>
          </AIResultHeader>
          <Box sx={{ mt: 2 }}>
            <Skeleton animation="wave" height={20} width="100%" />
            <Skeleton animation="wave" height={20} width="95%" />
            <Skeleton animation="wave" height={20} width="85%" />
            <Skeleton animation="wave" height={20} width="60%" sx={{ mt: 1 }} />
          </Box>
        </AIResultContainer>
      </ErrorBoundary>
    );
  }

  // Don't render if error or no data
  if (error || !data?.text) {
    return null;
  }

  const handleSourceClick = (source: SourceDocument) => {
    const sourceUrl = source.metadata?.source;
    if (sourceUrl) {
      try {
        const url = new URL(sourceUrl);
        const domain = process.env.DOMAIN || (typeof window !== 'undefined' ? window.location.origin : '');

        if (domain) {
          url.protocol = new URL(domain).protocol;
          url.host = new URL(domain).host;
        }

        window.open(url.toString(), '_self');
      } catch (err) {
        console.error('Error processing source URL:', err);
        window.open(sourceUrl, '_blank');
      }
    }
  };

  const handleContinueChat = () => {
    safeDataLayerPush({
      event: 'ai_search_continue_chat_clicked',
      isCustomEvent: 1,
      url: typeof window !== 'undefined' ? window.location?.pathname : undefined
    });
    if (data?.chatId) {
      window.dispatchEvent(
        new CustomEvent('openChatWithSession', {
          detail: {
            sessionId: data.chatId,
            query,
            answer: data.text,
            sourceDocuments: data.sourceDocuments
          }
        })
      );
    }
  };

  const handleDismiss = () => {
    safeDataLayerPush({
      event: 'ai_search_dismiss_clicked',
      isCustomEvent: 1,
      url: typeof window !== 'undefined' ? window.location?.pathname : undefined
    });
    setIsDismissed(true);
  };

  const handleViewMoreToggle = () => {
    if (!isExpanded) {
      safeDataLayerPush({
        event: 'ai_search_view_more_clicked',
        isCustomEvent: 1,
        url: typeof window !== 'undefined' ? window.location?.pathname : undefined
      });
    }
    setIsExpanded(!isExpanded);
  };

  const handleFeedbackSubmitted = (id: string) => {
    setFeedbackId(id);
    setShowTextFeedback(true);
  };

  const submitTextFeedback = async () => {
    if (!feedbackId || !apiHost || !feedbackText.trim()) return;

    setIsSubmittingText(true);

    try {
      const response = await fetch(`${apiHost}/api/v1/feedback/${feedbackId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          content: feedbackText.trim()
        })
      });

      if (!response.ok) {
        throw new Error(`Feedback API error: ${response.status}`);
      }

      setFeedbackSubmitted(true);
      setShowTextFeedback(false);
    } catch (err) {
      console.error('Error submitting text feedback:', err);
    } finally {
      setIsSubmittingText(false);
    }
  };

  return (
    <ErrorBoundary>
      <AIResultContainer data-testid="AISearchResult">
        <AIResultHeader>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AutoAwesomeIcon sx={{ fontSize: 20, color: 'primary.main' }} />
            <Typography variant="subtitle2" fontWeight={600}>
              {localization['aiSearch.title.label']?.shortTextValue || 'AI Answer'}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={handleDismiss}
            aria-label="Dismiss AI answer"
            sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </AIResultHeader>

        <AIResultContent ownerState={{ isExpanded }}>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              a: ({ href, children }) => (
                <MuiLink
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="primary"
                  sx={{ textDecoration: 'underline' }}>
                  {children}
                </MuiLink>
              ),
              p: ({ children }) => (
                <Typography variant="body2" component="p" sx={{ mb: 1.5, '&:last-child': { mb: 0 } }}>
                  {children}
                </Typography>
              ),
              ul: ({ children }) => (
                <Box component="ul" sx={{ pl: 2, mb: 1.5, '&:last-child': { mb: 0 } }}>
                  {children}
                </Box>
              ),
              ol: ({ children }) => (
                <Box component="ol" sx={{ pl: 2, mb: 1.5, '&:last-child': { mb: 0 } }}>
                  {children}
                </Box>
              ),
              li: ({ children }) => (
                <Typography variant="body2" component="li" sx={{ mb: 0.5 }}>
                  {children}
                </Typography>
              ),
              strong: ({ children }) => <strong>{children}</strong>,
              em: ({ children }) => <em>{children}</em>,
              code: ({ children }) => (
                <Box
                  component="code"
                  sx={{
                    backgroundColor: 'action.hover',
                    px: 0.5,
                    py: 0.25,
                    borderRadius: 0.5,
                    fontFamily: 'monospace',
                    fontSize: '0.875em'
                  }}>
                  {children}
                </Box>
              ),
              table: ({ children }) => (
                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={(theme) => ({
                    border: 1,
                    borderColor: 'divider',
                    overflowX: 'auto',
                    my: 2,
                    mx: -3, // Break out of parent padding (spacing(3) = 24px)
                    width: `calc(100% + ${theme.spacing(6)})`, // Account for parent padding on both sides
                    [theme.breakpoints.down('sm')]: {
                      mx: -2,
                      width: `calc(100% + ${theme.spacing(4)})`
                    }
                  })}>
                  <Table size="small" sx={{ minWidth: 300 }}>
                    {children}
                  </Table>
                </TableContainer>
              ),
              thead: ({ children }) => <TableHead>{children}</TableHead>,
              tbody: ({ children }) => <TableBody>{children}</TableBody>,
              tr: ({ children }) => <TableRow>{children}</TableRow>,
              th: ({ children }) => (
                <TableCell
                  component="th"
                  sx={{
                    fontWeight: 600,
                    backgroundColor: 'action.hover',
                    borderBottom: 1,
                    borderColor: 'divider'
                  }}>
                  {children}
                </TableCell>
              ),
              td: ({ children }) => (
                <TableCell sx={{ borderBottom: 1, borderColor: 'divider' }}>{children}</TableCell>
              )
            }}>
            {data.text}
          </ReactMarkdown>
        </AIResultContent>

        <Button
          variant="text"
          size="small"
          onClick={handleViewMoreToggle}
          sx={{ mt: 1, alignSelf: 'flex-start' }}>
          {isExpanded
            ? localization['aiSearch.viewLess.label']?.shortTextValue || 'View Less'
            : localization['aiSearch.viewMore.label']?.shortTextValue || 'View More'}
        </Button>

        {isExpanded && (
          <>
            {data.sourceDocuments && data.sourceDocuments.length > 0 && (
              <SourcesSection>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  {localization['aiSearch.sources.label']?.shortTextValue || 'Sources'}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {data.sourceDocuments.slice(0, 5).map((source, index) => (
                    <SourceChip
                      key={`source-${index}`}
                      label={source.metadata?.title || `Source ${index + 1}`}
                      size="small"
                      icon={<OpenInNewIcon sx={{ fontSize: '14px !important' }} />}
                      onClick={() => handleSourceClick(source)}
                      clickable
                    />
                  ))}
                </Box>
              </SourcesSection>
            )}

            <FeedbackSection>
              {/* Row 1: Continue Chat (left) and Feedback Thumbs or Thanks message (right) */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <Box>
                  {data?.chatId && (
                    <Button
                      variant="text"
                      size="small"
                      startIcon={<ChatIcon />}
                      onClick={handleContinueChat}>
                      {localization['aiSearch.continueChat.label']?.shortTextValue || 'Continue Chat'}
                    </Button>
                  )}
                </Box>
                {feedbackSubmitted ? (
                  <Typography variant="body2" color="text.secondary">
                    {localization['aiSearch.thanksFeedback.label']?.shortTextValue || 'Thanks for your feedback!'}
                  </Typography>
                ) : (
                  <AIFeedbackThumbs
                    messageId={data.chatMessageId}
                    apiHost={apiHost}
                    onFeedbackSubmitted={handleFeedbackSubmitted}
                  />
                )}
              </Box>
              {/* Row 2: Text field (center) and Submit button (right) */}
              {showTextFeedback && !feedbackSubmitted && (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 2, width: '100%' }}>
                  <TextField
                    size="small"
                    placeholder={
                      localization['aiSearch.feedbackPlaceholder.label']?.shortTextValue ||
                      'Tell us more (optional)'
                    }
                    value={feedbackText}
                    onChange={(e) => setFeedbackText(e.target.value)}
                    sx={{ flex: 1 }}
                  />
                  <Button
                    size="small"
                    variant="contained"
                    onClick={submitTextFeedback}
                    disabled={isSubmittingText || !feedbackText.trim()}>
                    {localization['aiSearch.sendFeedback.label']?.shortTextValue || 'Send'}
                  </Button>
                </Box>
              )}
            </FeedbackSection>
          </>
        )}
      </AIResultContainer>
    </ErrorBoundary>
  );
}

const AIResultContainer = styled(Box, {
  name: 'AISearchResult',
  slot: 'Container'
})(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50],
  borderRadius: Number(theme.shape.borderRadius) * 2,
  padding: theme.spacing(3),
  marginBottom: theme.spacing(4),
  border: `1px solid ${theme.palette.divider}`
}));

const AIResultHeader = styled(Box, {
  name: 'AISearchResult',
  slot: 'Header'
})(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1)
}));

const AIResultContent = styled(Box, {
  name: 'AISearchResult',
  slot: 'Content',
  shouldForwardProp: (prop) => prop !== 'ownerState'
})<{ ownerState: { isExpanded: boolean } }>(({ theme, ownerState }) => ({
  'marginTop': theme.spacing(2),
  'position': 'relative',
  '& p': {
    margin: 0
  },
  ...(!ownerState.isExpanded && {
    'maxHeight': '4.5em', // ~3 lines at 1.5 line-height
    'overflow': 'hidden',
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: '1.5em',
      background: `linear-gradient(transparent, ${theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50]})`
    }
  })
}));

const SourcesSection = styled(Box, {
  name: 'AISearchResult',
  slot: 'Sources'
})(({ theme }) => ({
  marginTop: theme.spacing(3),
  paddingTop: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`
}));

const SourceChip = styled(Chip, {
  name: 'AISearchResult',
  slot: 'SourceChip'
})(({ theme }) => ({
  'backgroundColor': theme.palette.background.paper,
  'border': `1px solid ${theme.palette.divider}`,
  '&:hover': {
    backgroundColor: theme.palette.action.hover
  }
}));

const FeedbackSection = styled(Box, {
  name: 'AISearchResult',
  slot: 'Feedback'
})(({ theme }) => ({
  marginTop: theme.spacing(2),
  paddingTop: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  flexDirection: 'column'
}));

export default AISearchResult;
